import { Flex } from 'native-base';
import React, { useEffect, useRef } from 'react';
import { Platform, StyleProp, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Color } from '~/components/color';
import { Spacing } from '~/components/spacing';
import { Text } from '~/components/text';
import { TriangleIcon } from '~/components/triangle-icon/triangle-icon';
import { announceForAccessibility } from '~/utils/accessibility';
import { isNative } from '~/utils/platform';

export type BannerNofificationType = 'default' | 'warning' | 'error';

type BannerNotificationProps = {
    text: string;
    type?: BannerNofificationType;
};

export const BannerNotification: React.FC<BannerNotificationProps> = props => {
    const { text, type } = props;

    const announcedTextRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (Platform.OS === 'ios' && text && announcedTextRef.current !== text) {
            announcedTextRef.current = text;
            announceForAccessibility(text);
        }
    }, [text]);

    let backgroundColor;
    let color;
    let triangle = null;

    switch (type) {
        case 'warning':
            backgroundColor = Color.SUBMARINE;
            color = Color.TEXT_DEFAULT;
            triangle = <TriangleIcon color={Color.ALMOST_BLACK} />;
            break;
        case 'error':
            backgroundColor = Color.WARNING_DEFAULT;
            color = Color.TEXT_TERTIARY;
            triangle = <TriangleIcon color={Color.TEXT_TERTIARY} />;
            break;
        default:
            backgroundColor = Color.BACKGROUND_PRIMARY;
            color = Color.TEXT_DEFAULT;
    }

    const { top } = useSafeAreaInsets();

    const bannerStyles: StyleProp<ViewStyle> = { padding: Spacing.SMALL };

    if (isNative()) {
        bannerStyles.paddingTop = top;
    }

    return (
        <Flex
            accessible
            accessibilityRole="alert"
            accessibilityLiveRegion={type === 'error' || type === 'warning' ? 'assertive' : 'polite'}
            alignItems="center"
            direction="column"
            style={{ backgroundColor, ...bannerStyles }}
        >
            <Flex direction="row">
                {triangle}
                <Text.PARAGRAPH_1 style={{ color }}>{text}</Text.PARAGRAPH_1>
            </Flex>
        </Flex>
    );
};
